import { Controller } from "@hotwired/stimulus";
import { getJSON } from "../api.js";

// Nawigacja główna klienta (C01–C05): zamykanie dropdownu, org, bump koszyka.
//
// Korzeń = <header id="ll-topnav"> (data-controller="topnav").
//
// (a) details[data-ll-dropdown] zamykane na klik poza elementem i na Esc
//     (Esc dodatkowo zwraca fokus do <summary>).
// (b) Nazwa aktywnej org pobrana raz z GET /api/acting-org/ → target "org".
//     Błąd API → target pozostaje pusty, nawigacja dalej działa.
// (c) MutationObserver na .ll-cart-count: zmiana wartości → is-full (gdy >0)
//     + is-bump (usuwana po animationend).
// (d) Linki główne (#ll-mainnav) zostają w pasku, dopóki się mieszczą. Gdy przycisk konta
//     wypada poza pasek, korzeń dostaje .is-nav-collapsed (CSS chowa linki i pokazuje ich
//     kopię .ll-acct-nav w menu konta). Pomiar: connect, resize (debounce 100 ms,
//     ResizeObserver), załadowanie fontów, zmiana zawartości portfela/koszyka. Przy pomiarze
//     klasa jest zdejmowana (ukryte linki nie mają szerokości) i od razu przywracana.
export default class extends Controller {
  static targets = ["org"];

  connect() {
    this._outsideClick = (e) => {
      // document-wide: the mobile vertical switcher lives in the page content, outside the header.
      document
        .querySelectorAll("details[data-ll-dropdown][open]")
        .forEach((d) => {
          if (!d.contains(e.target)) d.open = false;
        });
    };
    this._keydown = (e) => {
      if (e.key !== "Escape") return;
      const open = document.querySelector("details[data-ll-dropdown][open]");
      if (!open) return;
      open.open = false;
      open.querySelector("summary")?.focus();
    };
    document.addEventListener("click", this._outsideClick);
    document.addEventListener("keydown", this._keydown);

    this.#fetchOrg();
    this.#watchCart();
    this.#watchFit();
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick);
    document.removeEventListener("keydown", this._keydown);
    this._observer?.disconnect();
    this._fitObserver?.disconnect();
    this._ro?.disconnect();
    clearTimeout(this._fitTimer);
    window.removeEventListener("resize", this._onResize);
  }

  // --- private ---

  #watchFit() {
    this._onResize = () => {
      clearTimeout(this._fitTimer);
      this._fitTimer = setTimeout(() => this.#measureFit(), 100);
    };
    window.addEventListener("resize", this._onResize);
    if (typeof ResizeObserver !== "undefined") {
      this._ro = new ResizeObserver(this._onResize);
      this._ro.observe(this.element);
    }
    // Wallet balance / cart count arrive async and widen the bar.
    this._fitObserver = new MutationObserver(() => this.#measureFit());
    this.element
      .querySelectorAll(".ll-wallet, .ll-cart")
      .forEach((el) =>
        this._fitObserver.observe(el, {
          childList: true,
          characterData: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["hidden"],
        }),
      );
    document.fonts?.ready?.then?.(() => {
      if (this.element.isConnected) this.#measureFit();
    });
    this.#measureFit();
  }

  // Un-collapse, check whether the last item (account button) still ends inside the
  // content box, then set the class once. Layout is read synchronously, so no paint
  // happens between the two states.
  #measureFit() {
    if (!this.element.isConnected) return;
    const header = this.element;
    const acct = header.querySelector("#ll-account");
    if (!acct) return;
    header.classList.remove("is-nav-collapsed");
    const padRight = parseFloat(getComputedStyle(header).paddingRight) || 0;
    const limit = header.getBoundingClientRect().right - padRight;
    const overflow = acct.getBoundingClientRect().right > limit + 0.5;
    header.classList.toggle("is-nav-collapsed", overflow);
  }

  async #fetchOrg() {
    try {
      const data = await getJSON("/api/acting-org/");
      if (this.hasOrgTarget && data.acting?.name) {
        this.orgTarget.textContent = data.acting.name;
      }
    } catch {
      // Błąd API — target pozostaje pusty.
    }
  }

  #watchCart() {
    const countEl = this.element.querySelector(".ll-cart-count");
    if (!countEl) return;
    this._prevCount = countEl.textContent.trim();
    this._observer = new MutationObserver(() => {
      const next = countEl.textContent.trim();
      const n = parseInt(next, 10) || 0;
      countEl.classList.toggle("is-full", n > 0);
      if (next !== this._prevCount) {
        countEl.classList.remove("is-bump");
        countEl.classList.add("is-bump");
        countEl.addEventListener(
          "animationend",
          () => {
            countEl.classList.remove("is-bump");
          },
          { once: true },
        );
      }
      this._prevCount = next;
    });
    this._observer.observe(countEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }
}
